import React, {useCallback, useEffect, useMemo, useState} from "react";
import {SvgIcon, Tooltip} from "@mui/material";
import Api from "../../../../../../../core/services/api/api";
import Button from "../../../../../../components/base/button/button";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import GiftCardSearchSection from "../../../../../../components/search-sections/gift-cards";
import CreateGiftCardDialog from "../../../../../../components/dialogs/create-gift-card";
import ImportGiftCardsDialog from "../../../../../../components/dialogs/import-gift-cards";
import {Switch} from "../../../../../../components/base/switch";
import RefillGiftCardModal from "../../../../../../components/dialogs/refill-gift-card";
import AssignCustomerToGiftCardDialog from "../../../../../../components/dialogs/assign-customer-to-giftcard";
import {CustomerGiftCardTypeIds, DefaultPaginationInfo} from "../../../../../../../core/constants/enums";
import IconButton from "../../../../../../components/base/button/icon_button";
import {ChevronRightRounded, Edit} from "@mui/icons-material";
import EditGiftCardCategoryModal from "../../../../../../components/dialogs/edit-giftcard-category";
import {OperatorGiftCardsDataGridCellKeys} from "../../../../../../../core/constants/table/data-grid-column-keys";
import PartnerDataGrid, {dataGridCellTextProps, useDataGridSavedState} from "../../../../../../containers/data-grid";
import {DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes, DataGridColumnWidthTypes} from "../../../../../../../packages/data-grid";
import {ReactComponent as RemoveIcon} from "../../../../../../../assets/images/remove.svg";
import {formatMoney} from "../../../../../../../core/services/utils/utils";
import useSearch from "../../../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import DateUtils from "../../../../../../../core/services/date-utils";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {Link} from "react-router-dom";
import {routeFunctions} from "../../../../../../../core/models/static/routes/app-routes";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";

const dataGridSaveConfig = {
    name: 'GiftCardsListViewDataGrid',
    version: 2,
}

const exportingChunkSize = 50;

const GiftCardsList = () => {
    const [giftCardCategories, setGiftCardCategories] = useState([]);
    const [openImportFromExcel, setOpenImportFromExcel] = useState(false);
    const [openAddGiftCard, setOpenAddGiftCard] = useState(false);
    const [openRefillGiftCard, setOpenRefillGiftCard] = useState(false);
    const [openAssignCustomer, setOpenAssignCustomer] = useState(false);
    const [updateCategory, setUpdateCategory] = useState({open: false, giftCard: null});
    const [selectedGiftCard, setSelectedGiftCard] = useState();
    const [giftCards, setGiftCards] = useState([]);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('gift-card-list-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const operatorAccessLevels = useAccessLevels();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.giftCardsList);
        configureBehavioralTool((tags) => tags.giftCardsList);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Fetches the list of gift card categories from the server and if the result of the api is successful, sets the
     * list.
     */
    const getGiftCardCategories = useCallback(async () => {
        const response = await Api.getGiftCardCategories();
        if (!isMounted())
            return;
        if (response?.resultFlag)
            setGiftCardCategories(response.data);
    }, [isMounted])

    /**
     * Loads the list of gift card categories from the server as soon as the component mounts.
     */
    useEffect(() => {
        getGiftCardCategories().then();
    }, [getGiftCardCategories])

    /**
     * Memo version of data grid column
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: 'Card Number',
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                name: OperatorGiftCardsDataGridCellKeys.cardNumber,
                sortable: false,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.left,
                width: 200,
            },
            {
                title: 'Type',
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                name: OperatorGiftCardsDataGridCellKeys.type,
                sortable: false,
            },
            {
                title: 'Amount',
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                name: OperatorGiftCardsDataGridCellKeys.amount,
                sortable: false,
                width: 100,
            },
            {
                title: 'Category',
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                name: OperatorGiftCardsDataGridCellKeys.category,
                sortable: false,
                width: 250,
            },
            {
                title: 'Customer',
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                name: OperatorGiftCardsDataGridCellKeys.user,
                sortable: false,
                width: 200,
            },
            {
                title: 'Created Date',
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.date,
                name: OperatorGiftCardsDataGridCellKeys.createdDate,
                sortable: false,
            },
            {
                title: 'Active Date',
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.date,
                name: OperatorGiftCardsDataGridCellKeys.activeDate,
                sortable: false,
            },
            {
                title: 'Expiry Date',
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.date,
                name: OperatorGiftCardsDataGridCellKeys.expiryDate,
                sortable: false,
            },
            {
                title: 'Active',
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                name: OperatorGiftCardsDataGridCellKeys.active,
                sortable: false,
                width: 200,
            },
            ...(operatorAccessLevels.packages.sales.manageGiftCards ? [
                {
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    name: OperatorGiftCardsDataGridCellKeys.refill,
                    sortable: false,
                    pinned: true,
                    resizable: false,
                    visibilityToggleable: false,
                    pinnedType: DataGridColumnPinnedTypes.right,
                    width: {
                        size: 0,
                        type: DataGridColumnWidthTypes.default,
                        minWidth: 80
                    },
                },
                {
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    name: OperatorGiftCardsDataGridCellKeys.remove,
                    pinned: true,
                    resizable: false,
                    pinnedType: DataGridColumnPinnedTypes.right,
                    visibilityToggleable: false,
                    sortable: false,
                    width: 0,
                },
            ] : []),
        ], [operatorAccessLevels.packages.sales.manageGiftCards]);

    const [dataGridColumns, , dataGridPageSize, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
    }), [initialPaginationInfo]);

    /**
     * Gets the list of gift cards from server by calling the api regarding this action
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const forApi = {
            paginationInfo: args.paginationInfo,
            filters: {
                minimumAmount: args.filters?.minimum,
                maximumAmount: args.filters?.maximum,
                createDateStart: args.filters?.createdDateStart,
                createDateEnd: args.filters?.createdDateEnd,
                hasExpireDate: !!args.filters?.expiryDateStart || !!args.filters?.expiryDateEnd,
                expireDateStart: args.filters?.expiryDateStart,
                expireDateEnd: args.filters?.expiryDateEnd,
                categoryId: args.filters?.category ? JSON.parse(args.filters?.category).id : undefined,
                isActive: args.filters?.active,
                cardNumberKeyword: args.filters.keyword?.toString(),
            },
        };
        let response = await Api.searchGiftCards(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setGiftCards(response.data?.resultList ?? []);
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: response?.data?.paginationInfo?.length ?? 0
            }));
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [isMounted])

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const searchData = useSearch(search, initialSearchDataSettings)
    const canExportGiftCardsList = operatorAccessLevels.packages.sales.exportGiftCardsList;

    /**
     * Formats the list of active members into csv format
     * @type {(function(*=): {title: string, body: (*|*[])})}
     */
    const formatExportingData = useCallback((list) => {
        const exportTitle = `Gift Cards`;
        return {
            title: exportTitle,
            body: list?.map(item => ({
                'Card Number': item.cardNumber ?? '',
                'Type': item.type?.name ?? '',
                'Amount': formatMoney(item?.amount ?? 0) ?? '',
                'Category': item.category?.name ?? '',
                'Created Date': item.createDate ? DateUtils.serverToLocal(item.createDate).format(DateUtils.dateFormat) : '',
                'Active Date': item.activeDate ? DateUtils.serverToLocal(item.activeDate).format(DateUtils.dateFormat) : '',
                'Expiry Date': item.expireDate ? DateUtils.serverToLocal(item.expireDate).format(DateUtils.dateFormat) : '',
                'Customer': (item.lockForUser && !item.lockedCustomerInfo) ? 'Unknown' : item.lockedCustomerInfo ? (item.lockedCustomerInfo?.name ?? '') : 'Not Assigned',
                'Status': item.isActive ? 'Active' : 'Not Active',
            })) ?? [],
        }
    }, [])

    /**
     * Exports the entries of this view by searching over all the entries in the system.
     * @type {(function(): Promise<{title: string, body: (*|*[])}>)}
     */
    const exportToCsv = useCallback(async () => {
        const _paginationInfo = {
            length: searchData.paginationInfo.length,
            pageSize: exportingChunkSize,
            currentPage: 1,
        };
        let entries = [];
        while (searchData.paginationInfo.length > entries.length) {
            const res = await fetchEntries({...searchData, paginationInfo: _paginationInfo}, true);
            if (!res?.resultFlag) {
                return;
            }
            if (!res?.data?.resultList?.length) {
                break;
            }
            entries.push(...res.data.resultList);
            if (!isMounted())
                return;
            _paginationInfo.currentPage++;
        }
        return formatExportingData(entries);
    }, [fetchEntries, formatExportingData, isMounted, searchData])


    /**
     * Appends the newly created gift cards after they have been added to the server
     * @type {(function(*=): void)}
     */
    const onAddSuccess = useCallback((giftCards) => {
        if (!giftCards?.length)
            return;
        setGiftCards(prevState => [...giftCards, ...prevState].slice(0, searchData.paginationInfo.pageSize));
    }, [searchData.paginationInfo.pageSize])

    /**
     * Toggles the isActive status of the gift card by calling the api and if the result is successful, changes the
     * isActive status.
     * @type {(function(*, *): Promise<void>)}
     */
    const toggleIsActive = useCallback(async (giftCard, value) => {
        setGiftCards(prevState => prevState.map(e => e.cardNumber === giftCard.cardNumber ? {...e, loading: true} : e));
        const forApi = {
            id: giftCard.id,
            isActive: value,
        };
        const response = await Api.updateActivationStatusOfGiftCard(forApi);
        if (!isMounted())
            return;
        setGiftCards(prevState => prevState.map(e => e.cardNumber === giftCard.cardNumber ? {
            ...e,
            loading: false,
            isActive: response?.resultFlag ? value : e.isActive,
        } : e));
    }, [isMounted])

    /**
     * Removes a gift card from a list of gift cards by calling the server and only updating the state if the result
     * of the api call is successful.
     * @type {(function(*): Promise<void>)}
     */
    const removeGiftCard = useCallback(async (giftCard) => {
        const response = await Api.removeGiftCard(giftCard.cardNumber);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setGiftCards(prevState => prevState.filter(e => e.cardNumber !== giftCard.cardNumber))
        }
    }, [isMounted])

    /**
     * Opens the modal for refilling the selected gift card
     * @type {(function(*): void)}
     */
    const onRefillGiftCard = useCallback((giftCard) => {
        setSelectedGiftCard(giftCard);
        setOpenRefillGiftCard(true);
    }, [])

    /**
     * Sets the new gift card in the list with its new values.
     * @type {(function(*): void)}
     */
    const onEditGiftCardSuccess = useCallback((giftCard) => {
        setGiftCards(prevState => prevState.map((e) => e.cardNumber === giftCard.cardNumber ? {...e, ...giftCard} : e))
    }, [])

    /**
     * Opens the modal for assigning a customer to a gift card.
     * @type {(function(*): void)}
     */
    const onAssignCustomer = useCallback((giftCard) => {
        setSelectedGiftCard(giftCard);
        setOpenAssignCustomer(true);
    }, [])

    /**
     * Removes the assigned user from the gift card by calling the api regarding this action
     * @type {(function(*): Promise<void>)}
     */
    const removeAssignedCustomer = useCallback(async (giftCard) => {
        const response = await Api.removeLockForUserFromCard(giftCard.id);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setGiftCards(prevState => prevState.map(e => e.id === giftCard.id
                ? {...e, lockForUser: null, lockedCustomerInfo: null}
                : e
            ))
        }
    }, [isMounted])

    /**
     * Pops up confirmation dialog regarding removing the assigned user from the gift card
     * @type {(function(*): void)}
     */
    const getRemoveConfirmation = useCallback((giftCard) => {
        window.partner?.openConfirmationDialog(
            <div className={'p-2'}>
                <p className={'text-sm text-color-1 mb-4'}>
                    Are you sure you want to un-assign the current user from this gift card?
                </p>
            </div>,
            {},
            (decision) => decision && removeAssignedCustomer(giftCard)
        );
    }, [removeAssignedCustomer])


    /**
     * Memo version of table rows
     * @type {{data: *, key: *}[]}
     */
    const rows = useMemo(() => {
        return giftCards?.map((giftCard) => ({
            key: giftCard.id,
            data: giftCard,
            cells: {
                [OperatorGiftCardsDataGridCellKeys.cardNumber]: giftCard.cardNumber,
                [OperatorGiftCardsDataGridCellKeys.type]: giftCard.type?.name,
                [OperatorGiftCardsDataGridCellKeys.amount]: giftCard.amount,
                [OperatorGiftCardsDataGridCellKeys.category]:
                    !giftCard.category
                        ? <p>--</p>
                        : <>
                            <div className={'d-flex justify-content-start align-items-center flex-wrap'}>
                                <IconButton
                                    disabled={searchData.loading}
                                    className={'mr-1'}
                                    color={'secondary'}
                                    onPressed={() => setUpdateCategory({open: true, giftCard: giftCard})}
                                >
                                    <Edit className={'icon extraSmall'}/>
                                </IconButton>
                                <p {...dataGridCellTextProps} className={'text-sm'}>
                                    {giftCard.category?.name ?? ''}
                                </p>
                            </div>
                        </>,
                [OperatorGiftCardsDataGridCellKeys.createdDate]: giftCard.createDate,
                [OperatorGiftCardsDataGridCellKeys.expiryDate]: giftCard.expireDate,
                [OperatorGiftCardsDataGridCellKeys.activeDate]: giftCard.activeDate,
                [OperatorGiftCardsDataGridCellKeys.user]:
                    giftCard.lockForUser && !giftCard.lockedCustomerInfo
                        ? <p>Unknown</p>
                        : giftCard.lockedCustomerInfo
                            ? <div className={'d-flex align-items-center'}>
                                {
                                    operatorAccessLevels.packages.sales.manageGiftCards &&
                                    <Tooltip title={'Remove assigned customer'}>
                                        <IconButton

                                            onPressed={() => getRemoveConfirmation(giftCard)}
                                            color={'paper.error'}
                                            className={'remove-icon'}
                                        >
                                            <SvgIcon component={RemoveIcon} className='icon small'/>
                                        </IconButton>
                                    </Tooltip>
                                }
                                {
                                    operatorAccessLevels.packages.sales.customerGiftCards &&
                                    <Link
                                        className={'part-number-text-button ml-2'}
                                        to={routeFunctions.customer.generalInformation.paymentMethods(giftCard.lockedCustomerInfo?.id)}
                                        target={'_blank'}
                                    >
                                        <p className={'cursor-pointer-hover'}>{giftCard.lockedCustomerInfo?.name ?? ''}</p>
                                        <ChevronRightRounded className='icon small cursor-pointer-hover' color={"primary"}/>
                                    </Link>
                                }
                                {
                                    !operatorAccessLevels.packages.sales.customerGiftCards &&
                                    <div className={'part-number-text-button ml-2'}>
                                        <p>{giftCard.lockedCustomerInfo?.name ?? ''}</p>
                                    </div>
                                }
                            </div>
                            : operatorAccessLevels.packages.sales.manageGiftCards
                                ? <Button
                                    color={'primary'}
                                    variant={'outlined'}
                                    onClick={() => onAssignCustomer(giftCard)}
                                >
                                    Assign
                                </Button>
                                : '--',
                [OperatorGiftCardsDataGridCellKeys.active]:
                    <div>
                        <Switch
                            color={'primary'}
                            disabled={!!giftCard.loading || !operatorAccessLevels.packages.sales.manageGiftCards}
                            checked={giftCard.isActive}
                            onChange={(checked) => toggleIsActive(giftCard, checked)}
                        />
                    </div>,
                [OperatorGiftCardsDataGridCellKeys.refill]:
                    giftCard?.type?.id === CustomerGiftCardTypeIds.refillable &&
                    <Button
                        variant={'outlined'}
                        color={'primary'}
                        onClick={() => onRefillGiftCard(giftCard)}
                    >
                        Refill
                    </Button>,
                [OperatorGiftCardsDataGridCellKeys.remove]:
                    <Tooltip title={'Remove Gift Card'}>
                        <IconButton

                            onPressed={() => removeGiftCard(giftCard)}
                            color={'paper.error'}
                            className={'remove-icon'}
                        >
                            <SvgIcon component={RemoveIcon} className='icon normal'/>
                        </IconButton>
                    </Tooltip>,
            }
        }))
    }, [giftCards, searchData.loading, operatorAccessLevels.packages.sales.manageGiftCards, operatorAccessLevels.packages.sales.customerGiftCards, getRemoveConfirmation, onAssignCustomer, toggleIsActive, onRefillGiftCard, removeGiftCard])


    return (
        <>
            <GiftCardSearchSection
                searchData={searchData}
                giftCardCategories={giftCardCategories}
                openCreateGiftCard={setOpenAddGiftCard}
                openImportFromExcel={setOpenImportFromExcel}
            />
            <div className='my-4 w-100'/>
            <PartnerDataGrid
                {...dataGridSaveConfig}
                initialLayoutState={{
                    columns: columns,
                }}
                columns={dataGridColumns}
                rows={rows}
                state={{
                    pagination: searchData.paginationInfo,
                    loading: {state: searchData.loading},
                    density: dataGridDensity,
                }}
                getExportInfo={canExportGiftCardsList ? exportToCsv : undefined}
                onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
            />
            <CreateGiftCardDialog
                open={openAddGiftCard}
                setOpen={setOpenAddGiftCard}
                onSuccess={(giftCard) => onAddSuccess([giftCard])}
            />
            <AssignCustomerToGiftCardDialog
                open={openAssignCustomer}
                setOpen={setOpenAssignCustomer}
                onSuccess={onEditGiftCardSuccess}
                giftCard={selectedGiftCard}
            />
            <RefillGiftCardModal
                open={openRefillGiftCard}
                setOpen={setOpenRefillGiftCard}
                giftCard={selectedGiftCard}
                onSuccess={onEditGiftCardSuccess}
            />
            <ImportGiftCardsDialog
                open={openImportFromExcel}
                setOpen={setOpenImportFromExcel}
                giftCardCategories={giftCardCategories}
                onSuccess={onAddSuccess}
            />
            <EditGiftCardCategoryModal
                open={updateCategory.open}
                setOpen={(open) => setUpdateCategory(({open: open}))}
                giftCardCategories={giftCardCategories}
                giftCard={updateCategory.giftCard}
                onSuccess={onEditGiftCardSuccess}
            />
        </>
    );
}

export default GiftCardsList;

